import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import { ARCHIVE_AFTER_DAYS, daysSince } from "../../../Services/familyNewsService";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value, "YYYY-MM-DD");
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "-";
};

/** Range types (retreats, reunions) read as "start - end"; the rest as one day. */
const formatEventDate = (item) => {
  const start = formatDate(item?.date);
  if (!item?.end_date || item.end_date === item.date) return start;
  return `${start} - ${formatDate(item.end_date)}`;
};

const PHOTO_ROTATE_MS = 3500;

/** "Day Cruise, Dinner, Movie" -> one entry per line; commas are the break. */
const splitActivities = (value) =>
  String(value || "")
    .split(",")
    .map((activity) => activity.trim())
    .filter(Boolean);

/** Cards holding more than one photo cycle through them on their own. */
const CardPhotos = ({ item }) => {
  const photos = item.photos || [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (photos.length < 2 || paused) return undefined;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % photos.length),
      PHOTO_ROTATE_MS
    );
    return () => clearInterval(timer);
  }, [photos.length, paused]);

  if (photos.length === 0) {
    return (
      <img
        src={window.getPublicUrl("/assets/images/card-5.svg")}
        className="event-img event-img-in-wrapper"
        alt={item.title || "Family news"}
      />
    );
  }

  // Removing photos can leave the index past the end of a now shorter list.
  const activeIndex = index % photos.length;

  return (
    <div
      className="family-news-card-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, position) => (
        <img
          key={photo.id || position}
          src={photo.dataUrl}
          className={`event-img event-img-in-wrapper family-news-card-slide${
            position === activeIndex ? " is-active" : ""
          }`}
          alt={photo.name || item.title || "Family news"}
        />
      ))}
    </div>
  );
};

/** Each host carries its own line color: "Barbara · Red & Debra · Orange". */
const HostsWithLineColors = ({ people }) => {
  const hosts = (people || []).filter((person) => person?.name);
  if (hosts.length === 0) return <>-</>;
  return (
    <>
      {hosts.map((person, index) => (
        <React.Fragment key={`${person.name}-${index}`}>
          {index > 0 ? " & " : ""}
          {person.name}
          {person.color_name ? (
            <>
              {" · "}
              <span style={{ color: person.hex_code || "#000000" }}>
                {person.color_name}
              </span>
            </>
          ) : null}
        </React.Fragment>
      ))}
    </>
  );
};

const byName = (a, b) =>
  String(a || "")
    .toLowerCase()
    .localeCompare(String(b || "").toLowerCase(), undefined, {
      sensitivity: "base",
      numeric: true,
    });

/** Attendees group under one heading per line color: the line is named once,
    on its own line, carrying its own count, with its members listed under it.
    Groups run alphabetically by line, attendees with no line come last. */
const groupAttendeesByColor = (people) => {
  const groups = [];
  const positions = new Map();
  (people || []).forEach((person) => {
    if (!person?.name) return;
    const label = person.color_name ? `${person.color_name} Line` : "";
    const key = label || "__no-line__";
    if (!positions.has(key)) {
      positions.set(key, groups.length);
      groups.push({
        key,
        label,
        hex: person.hex_code || "#000000",
        names: [],
      });
    }
    groups[positions.get(key)].names.push(person.name);
  });
  groups.forEach((group) => group.names.sort(byName));
  return groups.sort((a, b) => {
    if (!a.label) return 1;
    if (!b.label) return -1;
    return byName(a.label, b.label);
  });
};

function FamilyNewsList({
  news,
  loading,
  isSearching,
  openAddEditModal,
  openDeleteModal,
  onOpenPhotos,
}) {
  if (loading) {
    return (
      <div className="events-content">
        <div className="text-center">
          <CircularProgress size={30} color="inherit" />
        </div>
      </div>
    );
  }

  if (!news || news.length === 0) {
    return (
      <div className="events-content">
        <div className="empty-events-state text-center">
          <img
            src={window.getPublicUrl("/assets/images/event-bg-img.png")}
            alt="No family news"
            className="empty-events-img mx-auto mb-4"
          />
          <p className="empty-events-text mb-4">
            {isSearching
              ? "No family news matches your search."
              : "No family news has been posted yet."}
          </p>
          {/* The top controls already carry an Add button while searching. */}
          {!isSearching && (
            <button
              className="events-add-btn"
              onClick={() => openAddEditModal("Add", null)}
            >
              + Add Family News
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="events-content">
      <div className="events-scroll-area">
        <div className="event-grid">
          {news.map((item) => {
            const daysOld = daysSince(item.end_date || item.date);
            const daysLeft = ARCHIVE_AFTER_DAYS - daysOld;
            return (
              <div className="event-card event-card-my-invitations" key={item.id}>
                <div
                  className="event-card-image-wrapper family-news-card-image"
                  onClick={() => item.photos?.length > 0 && onOpenPhotos(item)}
                  style={{
                    position: "relative",
                    flex: "0 0 48%",
                    minHeight: "48%",
                    width: "100%",
                    overflow: "hidden",
                    cursor: item.photos?.length > 0 ? "pointer" : "default",
                  }}
                >
                  <CardPhotos item={item} />
                  {item.photos?.length > 1 && (
                    <span className="family-news-photo-badge">
                      +{item.photos.length - 1} more
                    </span>
                  )}
                </div>

                <div className="card-body" style={{ flexDirection: "column" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "flex-start",
                      flex: 1,
                      flexDirection: "column",
                      alignItems: "stretch",
                      width: "100%",
                      minHeight: 0,
                    }}
                  >
                    <div
                      className="event-details-formatted"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <div className="event-detail-row">
                        <span className="event-detail-label">
                          Host(s) &amp; Line Color:
                        </span>
                        <span className="event-detail-value">
                          <HostsWithLineColors people={item.hosts} />
                        </span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Type of Event:</span>
                        <span className="event-detail-value">{item.title || "-"}</span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Theme:</span>
                        <span className="event-detail-value">{item.theme || "-"}</span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Event Date:</span>
                        <span className="event-detail-value">
                          {formatEventDate(item)}
                        </span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">Location:</span>
                        <span className="event-detail-value location-value">
                          {item.location || "-"}
                        </span>
                      </div>
                      <div className="event-detail-row">
                        <span className="event-detail-label">
                          Participated Activities:
                        </span>
                        <span className="event-detail-value">
                          {splitActivities(item.activities).length ? (
                            <ul className="family-news-activities-list">
                              {splitActivities(item.activities).map(
                                (activity, index) => (
                                  <li key={`${activity}-${index}`}>{activity}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>
                      {/* The honoree's/host's own words read as one more
                          label/value row, like the fields above it; the note
                          keeps its own line breaks in the value column. */}
                      <div className="event-detail-row">
                        <span className="event-detail-label">
                          Words (Honoree and/or Host):
                        </span>
                        <span className="event-detail-value family-news-words-value">
                          {item.words || "-"}
                        </span>
                      </div>

                      {/* Attendance and guests fill the lower half of the card
                          as two left-justified vertical lists. */}
                      <div className="family-news-people-columns">
                        <div className="family-news-people-column">
                          <div className="family-news-people-heading">
                            FMs in Attendance ({item.attendees?.length || 0})
                          </div>
                          {item.attendees?.length ? (
                            /* Attendees read grouped by line: the line heads
                               the group and carries the only count, with its
                               members listed under it. */
                            <div className="family-news-people-groups">
                              {groupAttendeesByColor(item.attendees).map(
                                (group) => (
                                  <div
                                    className="family-news-people-group"
                                    key={group.key}
                                  >
                                    {/* Only the line heading carries the
                                        colour; attendees with no line list
                                        plain, with no heading above them. */}
                                    {group.label ? (
                                      <div
                                        className="family-news-people-group-color"
                                        style={{ color: group.hex }}
                                      >
                                        {group.names.length > 1
                                          ? `${group.label} (${group.names.length})`
                                          : group.label}
                                      </div>
                                    ) : null}
                                    {/* The names under the heading read
                                        black. */}
                                    <ul className="family-news-people-list family-news-people-group-names">
                                      {group.names.map((name, index) => (
                                        <li key={`${name}-${index}`}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="family-news-people-empty">-</p>
                          )}
                        </div>
                        <div className="family-news-people-column">
                          <div className="family-news-people-heading">Guests</div>
                          {item.guests?.length ? (
                            <ul className="family-news-people-list">
                              {item.guests.map((guest, index) => (
                                <li key={`${guest}-${index}`}>{guest}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="family-news-people-empty">-</p>
                          )}
                        </div>
                      </div>
                      {item.archived && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Folder:</span>
                          <span className="event-detail-value">{item.folder}</span>
                        </div>
                      )}

                      <div className="view-icon invitation-card-actions">
                        <div className="cursor-pointer icon-tooltip-wrapper">
                          <span
                            className="icon-btn"
                            onClick={() => openAddEditModal("Edit", item)}
                          >
                            <img
                              src={window.getPublicUrl("/assets/images/edit-icon.svg")}
                              alt="Edit"
                            />
                          </span>
                          <div className="icon-tooltip">Edit news</div>
                        </div>
                        <div className="cursor-pointer icon-tooltip-wrapper">
                          <span
                            className="icon-btn"
                            onClick={() => openAddEditModal("View", item)}
                          >
                            <img
                              src={window.getPublicUrl("/assets/images/eye-icon.svg")}
                              alt="View"
                            />
                          </span>
                          <div className="icon-tooltip">View news</div>
                        </div>
                        <div className="cursor-pointer icon-tooltip-wrapper">
                          <span
                            className="icon-btn"
                            onClick={() => openDeleteModal(item)}
                          >
                            <img
                              src={window.getPublicUrl("/assets/images/deleteEvent.svg")}
                              alt="Delete"
                            />
                          </span>
                          <div className="icon-tooltip">Delete news</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 0,
                      paddingTop: "16px",
                      borderTop: "1px solid #e0e0e0",
                      width: "100%",
                    }}
                  >
                    <p
                      style={{
                        color: "#808080",
                        fontSize: "12px",
                        margin: 0,
                        lineHeight: 1.2,
                        textAlign: "center",
                      }}
                    >
                      {item.archived
                        ? `Archived in "${item.folder}".`
                        : daysLeft > 0
                        ? `Archives to "${item.folder}" in ${daysLeft} day${
                            daysLeft === 1 ? "" : "s"
                          }.`
                        : `Archives to "${item.folder}" today.`}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default FamilyNewsList;
